import { Box, Button, Typography } from "@mui/material";
import webFormsStyles from "../WebForms.styles";
import webFormsTokens, { webFormsResponsiveTokens } from "../WebForms.tokens";
import { PlusCircleGlyph } from "./WebFormsIcons";
import { tourAttr } from "tour";

const styles = webFormsStyles;
const tokens = webFormsTokens;
const { phone, tablet } = webFormsResponsiveTokens;

interface WebFormHeaderProps {
  onCreateClick: () => void;
  /** Locked out by the plan: the page still renders, the action does not act. */
  createDisabled?: boolean;
  /** Drives the "<b>N</b> web forms" line the references show below `md`. */
  totalCount: number;
}

/**
 * Title block plus the single header action, laid out like the Sign Requests
 * header. There is only one way to create a web form, so this is a plain
 * button rather than that screen's split button.
 *
 * Below `sm` the reference drops the title (the app bar carries it) and the
 * wide button gives way to a full-width Create under the header — the same
 * control Sign Requests and Templates use on a phone.
 */
const WebFormHeader = ({
  onCreateClick,
  createDisabled,
  totalCount,
}: WebFormHeaderProps) => (
  <>
    <Box sx={styles.header}>
      <Box sx={styles.headerText}>
        <Typography component="h1" sx={styles.headerTitle}>
          Web Forms
        </Typography>
        <Typography component="p" sx={styles.headerSubtitle}>
          Turn a template into a shareable form — send one link and collect
          signed copies.
        </Typography>
        {/*
          Phone and tablet trade the strapline for the running tally, exactly
          as `Webforms - Mobile & Tablet.dc.html` does.
        */}
        <Typography component="p" sx={styles.headerCount}>
          <Box component="span" sx={styles.headerCountValue}>
            {totalCount}
          </Box>{" "}
          web forms
        </Typography>
      </Box>

      <Box sx={styles.headerActions} {...tourAttr("webforms.create")}>
        <Button
          disableRipple
          variant="contained"
          disabled={createDisabled}
          onClick={onCreateClick}
          sx={styles.primaryButton}
        >
          <PlusCircleGlyph
            sx={{
              width: {
                sm: tablet.primaryButton.iconSize,
                md: tokens.primaryButton.iconSize,
              },
              height: {
                sm: tablet.primaryButton.iconSize,
                md: tokens.primaryButton.iconSize,
              },
            }}
          />
          Create Web Form
        </Button>
      </Box>
    </Box>

    {/*
      Phone "Create" — full width under the header, replacing the square button
      the reference put beside the count line and the pill it floated over the
      list. It carries the same tour marker as the desktop button: only one of
      the two is ever visible, and the resolver skips the hidden one.
    */}
    <Button
      disableRipple
      disabled={createDisabled}
      onClick={onCreateClick}
      sx={styles.createMobile}
      {...tourAttr("webforms.create")}
    >
      <PlusCircleGlyph
        sx={{
          width: phone.createButton.iconSize,
          height: phone.createButton.iconSize,
        }}
      />
      Create Web Form
    </Button>
  </>
);

export default WebFormHeader;
